import {useMemo, useState} from 'react';
import {Box} from 'src/design/atoms/Box';
import {Stack} from 'src/design/atoms/Stack';
import {IconChartBar} from '@tabler/icons-react';
import {AssistantAnswerQualitySection} from 'src/app/assistant/ControlCenter/AssistantAnswerQualitySection';
import {AssistantConversationsTable} from 'src/app/assistant/ControlCenter/AssistantConversationsTable';
import {AssistantFeedbackTable} from 'src/app/assistant/ControlCenter/AssistantFeedbackTable';
import {AssistantTranscriptDrawer} from 'src/app/assistant/ControlCenter/AssistantTranscriptDrawer';
import {AssistantUsageChart} from 'src/app/assistant/ControlCenter/AssistantUsageChart';
import {AssistantUsageSection} from 'src/app/assistant/ControlCenter/AssistantUsageSection';
import {
  formatTokens,
  formatUsd,
} from 'src/app/assistant/ControlCenter/assistantUsageFormat';
import {SERIES_DAYS} from 'src/app/assistant/ControlCenter/controlCenterParams';
import type {
  ControlCenterView,
  OpenTranscript,
} from 'src/app/assistant/ControlCenter/controlCenterParams';
import {useControlCenterView} from 'src/app/assistant/ControlCenter/useControlCenterUrlState';
import {SelectBox} from 'src/design/atoms/SelectBox';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {
  useGlobalAssistantOverviewQuery,
  useGlobalAssistantTranscriptQuery,
  useGlobalAssistantUsageSeriesQuery,
  useGlobalAssistantUserUsageQuery,
} from 'src/redux/queries/assistantControlCenter';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import type {ISelectOption} from 'src/types/Form';
import {AssistantInstanceUsageTable} from './components/AssistantInstanceUsageTable';

export const GlobalAssistantControlCenterPage = () => {
  useSetDocumentTitle('Scout Usage');

  const {view, selectView} = useControlCenterView();
  const [instanceId, setInstanceId] = useState<number | undefined>(undefined);
  const [openTranscript, setOpenTranscript] = useState<OpenTranscript | null>(
    null
  );

  const {
    data: overview,
    isLoading: overviewLoading,
    isError: overviewError,
  } = useGlobalAssistantOverviewQuery();
  const {
    data: series,
    isFetching: seriesFetching,
    isError: seriesError,
  } = useGlobalAssistantUsageSeriesQuery({days: SERIES_DAYS, instanceId});
  const {data: users} = useGlobalAssistantUserUsageQuery({
    days: SERIES_DAYS,
    instanceId,
  });

  const {
    data: transcript,
    isFetching: transcriptFetching,
    isError: transcriptError,
  } = useGlobalAssistantTranscriptQuery(openTranscript?.conversationId ?? 0, {
    skip: openTranscript === null,
  });

  const instanceOptions = useMemo<ISelectOption[]>(
    () =>
      (overview?.instances ?? []).map(i => ({
        id: i.instanceId,
        label: i.instanceName ?? `Instance ${i.instanceId}`,
      })),
    [overview]
  );

  const userOptions = useMemo<ISelectOption[]>(
    () =>
      (users ?? []).map(u => ({
        id: u.userId,
        label: u.userName ?? `User ${u.userId}`,
      })),
    [users]
  );

  const activeInstances = (overview?.instances ?? []).filter(
    i => i.last30Days.totalTokens > 0
  ).length;

  const usageStats = overview
    ? [
        {label: 'Tokens today', value: formatTokens(overview.today.totalTokens)},
        {label: 'Cost today', value: formatUsd(overview.today.estimatedCostUsd)},
        {
          label: 'Cost (30d)',
          value: formatUsd(overview.last30Days.estimatedCostUsd),
        },
        {label: 'Active instances', value: activeInstances},
        {
          label: 'Conversations (30d)',
          value: overview.last30ConversationCount,
        },
      ]
    : [];

  const {RenderedTabsComponent} = useUrlQueryTabV2<ControlCenterView>({
    view,
    defaultView: 'overview',
    onSelectView: selectView,
    tabs: [
      {
        view: 'overview',
        label: 'Overview',
        component: (
          <Stack gap="lg">
            <AssistantUsageSection
              stats={usageStats}
              isLoading={overviewLoading}
              isError={overviewError}>
              <AssistantUsageChart
                series={series}
                isLoading={seriesFetching}
                isError={seriesError}
              />
            </AssistantUsageSection>
            <AssistantAnswerQualitySection
              tally={overview?.feedback}
              isLoading={overviewLoading}
              isError={overviewError}
              scopeText="the last 30 days across all instances"
            />
            <SectionCard title="Instances">
              <AssistantInstanceUsageTable
                data={overview?.instances ?? []}
                isLoading={overviewLoading}
                isError={overviewError}
              />
            </SectionCard>
          </Stack>
        ),
      },
      {
        view: 'conversations',
        label: 'Conversations',
        component: (
          <SectionCard title="Conversations">
            <AssistantConversationsTable
              scope="global"
              instanceId={instanceId}
              userOptions={userOptions}
              showInstanceColumn
              onViewTranscript={id => setOpenTranscript({conversationId: id})}
            />
          </SectionCard>
        ),
      },
      {
        view: 'feedback',
        label: 'Feedback',
        component: (
          <SectionCard title="Feedback">
            <AssistantFeedbackTable
              scope="global"
              instanceId={instanceId}
              userOptions={userOptions}
              showInstanceColumn
              onViewConversation={(conversationId, focusMessageId) =>
                setOpenTranscript({conversationId, focusMessageId})
              }
            />
          </SectionCard>
        ),
      },
    ],
  });

  return (
    <ContentWrapper>
      <Stack gap="lg">
        <PageHeader>
          <PageHeader.Title
            icon={<IconChartBar size={24} />}
            subtitle="Platform-wide Scout token usage, cost, reply feedback, and activity">
            Scout Usage
          </PageHeader.Title>
        </PageHeader>

        <Box w={280}>
          <SelectBox
            label="Instance"
            placeholder="All instances"
            ariaLabel="Filter by instance"
            options={instanceOptions}
            value={instanceId !== undefined ? String(instanceId) : undefined}
            onSelectChangeCallback={val =>
              setInstanceId(val ? Number(val) : undefined)
            }
          />
        </Box>

        <Box>{RenderedTabsComponent}</Box>
      </Stack>

      <AssistantTranscriptDrawer
        opened={openTranscript !== null}
        onClose={() => setOpenTranscript(null)}
        transcript={transcript}
        isLoading={transcriptFetching}
        isError={transcriptError}
        showInstance
        focusMessageId={openTranscript?.focusMessageId}
      />
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.AdminAssistantControlCenter(),
  element: <GlobalAssistantControlCenterPage />,
  isPrivate: true,
  requireSuperAdmin: true,
});
